import { React, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { Button, Alert } from "@mui/material";
import GlobalLoadingSpinner from "../../components/GlobalLoadingSpinner";
import { api } from "../../utils/api";
import { isAdministratorPortal, XeroHealthStatusPanel } from "../../components/XeroIntegrationHealth";

const apiErrorText = (response, fallback) => {
  const data = response?.data;
  return (
    data?.message ||
    data?.error ||
    data?.data?.error ||
    (typeof data === "string" && data) ||
    (response?.status ? `Request failed (${response.status})` : null) ||
    fallback
  );
};

const XeroSettings = () => {
  const adminPortal = isAdministratorPortal();
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncingPayments, setSyncingPayments] = useState(false);
  const [status, setStatus] = useState(null);
  const [banner, setBanner] = useState(null);
  const [syncResult, setSyncResult] = useState(null);
  const [paymentSyncResult, setPaymentSyncResult] = useState(null);

  const ensureAccessToken = async () => {
    const token = await getAccessTokenSilently();
    if (!token) {
      throw new Error("You are not signed in. Refresh the page and try again.");
    }
    sessionStorage.setItem("token", token);
    return token;
  };

  const fetchStatus = async () => {
    setLoading(true);
    try {
      await ensureAccessToken();
      const response = await api("v1/xeroapi/status", "GET");
      setStatus(response?.data?.data || null);
      if (response?.data?.success) {
        setBanner({ severity: "success", text: response.data.message });
      } else {
        setBanner({
          severity: "warning",
          text: apiErrorText(response, "Could not check Xero status."),
        });
      }
    } catch (error) {
      setBanner({
        severity: "error",
        text: apiErrorText(error?.response, error.message || "Could not check Xero status."),
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!adminPortal) return;
    const xeroAuth = searchParams.get("xeroAuth");
    if (xeroAuth === "success") {
      setBanner({ severity: "success", text: "Xero reconnected. Tokens have been saved for this region." });
      setSearchParams({});
    } else if (xeroAuth === "error") {
      setBanner({
        severity: "error",
        text: searchParams.get("message") || "Xero authentication failed.",
      });
      setSearchParams({});
    }
    if (isAuthenticated) {
      fetchStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, adminPortal]);

  if (!adminPortal) {
    return (
      <div className="page w-full flex flex-column items-center p-8 gap-4">
        <Alert severity="info" className="w-full max-w-3xl">
          Xero integration settings are only available in the Administrator portal.
        </Alert>
      </div>
    );
  }

  const handleReconnect = async () => {
    setLoading(true);
    try {
      await ensureAccessToken();
      const response = await api("v1/xeroapi/consentUrl", "GET");
      const consentUrl = response?.data?.data?.consentUrl;
      if (!consentUrl) {
        setBanner({
          severity: "error",
          text: apiErrorText(response, "Could not generate the Xero login URL."),
        });
        setLoading(false);
        return;
      }
      window.location.href = consentUrl;
    } catch (error) {
      setBanner({
        severity: "error",
        text: apiErrorText(error?.response, error.message || "Could not start Xero reconnect."),
      });
      setLoading(false);
    }
  };

  const handleSyncSelfSignupPayments = async () => {
    setSyncingPayments(true);
    setPaymentSyncResult(null);
    try {
      await ensureAccessToken();
      const response = await api("v1/xeroapi/sync-selfsignup-payments", "POST");
      setPaymentSyncResult(response?.data?.data || null);
      setBanner({
        severity: response?.data?.success ? "success" : "warning",
        text: response?.data?.message || "Self-signup payment sync finished.",
      });
    } catch (error) {
      setBanner({
        severity: "error",
        text: apiErrorText(error?.response, error.message || "Self-signup payment sync failed."),
      });
    } finally {
      setSyncingPayments(false);
    }
  };

  const handleSyncMerchants = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      await ensureAccessToken();
      const response = await api("v1/xeroapi/sync-merchant-contacts", "POST");
      setSyncResult(response?.data?.data || null);
      setBanner({
        severity: response?.data?.success ? "success" : "warning",
        text: response?.data?.message || "Sync finished.",
      });
    } catch (error) {
      setBanner({
        severity: "error",
        text: error?.response?.data?.message || error.message || "Merchant contact sync failed.",
      });
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="page w-full flex flex-column items-center p-8 gap-4">
      <div className="pageTitle flex flex-col w-full text-left mb-4">
        <h1>Xero</h1>
        <p className="text-slate-600 mt-2">
          Redemption verify creates Xero bills against the merchant contact. If the connection is expired,
          reconnect here, then sync merchant contacts.
        </p>
      </div>

      {banner && (
        <Alert severity={banner.severity} className="w-full max-w-3xl">
          {banner.text}
        </Alert>
      )}

      <XeroHealthStatusPanel currentPortal="Administrator" />

      {loading ? (
        <GlobalLoadingSpinner />
      ) : (
        <div className="w-full max-w-3xl flex flex-col gap-4">
          <div className="border rounded-md p-4 bg-white shadow-sm">
            <h3 className="text-lg font-bold mb-2">Connection</h3>
            <p>
              Region: <strong>{status?.region || "—"}</strong>
            </p>
            <p>
              Status:{" "}
              <strong>{status?.connected ? "Connected" : "Not connected / expired"}</strong>
            </p>
            <p>
              Organisation: <strong>{status?.orgName || "—"}</strong>
              {status?.orgCountry ? ` (${status.orgCountry})` : ""}
            </p>
            <p>
              Last token update:{" "}
              <strong>
                {status?.lastModified ? new Date(status.lastModified).toLocaleString() : "—"}
              </strong>
            </p>
            <div className="flex gap-2 mt-4">
              <Button variant="contained" onClick={handleReconnect}>
                Reconnect Xero
              </Button>
              <Button variant="outlined" onClick={fetchStatus}>
                Recheck
              </Button>
            </div>
          </div>

          <div className="border rounded-md p-4 bg-white shadow-sm">
            <h3 className="text-lg font-bold mb-2">Merchant contacts</h3>
            <p className="mb-4">
              Creates or updates a Xero contact for each active merchant account and stores the contact id
              used when verifying a redemption.
            </p>
            <Button
              variant="contained"
              color="primary"
              disabled={!status?.connected || syncing}
              onClick={handleSyncMerchants}
            >
              {syncing ? "Syncing…" : "Sync merchant contacts to Xero"}
            </Button>
            {syncResult?.results?.length > 0 && (
              <ul className="mt-4 list-disc pl-5">
                {syncResult.results.map((row) => (
                  <li key={row.name} className={row.ok ? "text-green-700" : "text-red-700"}>
                    {row.name}: {row.ok ? "synced" : row.error}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="border rounded-md p-4 bg-white shadow-sm">
            <h3 className="text-lg font-bold mb-2">Self-signup payments</h3>
            <p className="mb-4">
              Backfills customer invoices and Stripe payments in Xero for self-signup transactions
              that never got a Xero invoice. Invoice and payment dates use the original signup date.
            </p>
            <Button
              variant="contained"
              color="primary"
              disabled={!status?.connected || syncingPayments}
              onClick={handleSyncSelfSignupPayments}
            >
              {syncingPayments ? "Backfilling…" : "Backfill missing self-signup payments"}
            </Button>
            {paymentSyncResult?.results?.length > 0 && (
              <ul className="mt-4 list-disc pl-5">
                {paymentSyncResult.results.map((row) => (
                  <li key={row.id || row.name} className={row.ok ? "text-green-700" : "text-red-700"}>
                    {row.name}: {row.ok ? `synced ${row.invoiceNumber || ""}`.trim() : row.error}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default XeroSettings;
